import { Brain, Heart, Dumbbell, Sparkles, Eye, GalleryVerticalEnd } from "lucide-react";

const pillars = [
  {
    icon: Brain,
    label: "Mental",
    range: "Days 01 — 07",
    intro: "The focusing of the mind.",
    days: [
      "Setting the mind — The Focusing of the Mind",
      "First mental step — The Concentration of the Beginning Action",
      "Second mental step — The Process of Movement",
      "Third mental step — The Interaction of the Movement",
      "Fourth mental step — The Continuation of the Movement",
      "Fifth mental step — The Rejection of Adversity",
      "Sixth mental step — The Achievement of the Action",
    ],
  },
  {
    icon: Heart,
    label: "Emotional",
    range: "Days 08 — 14",
    intro: "Scaling desires over failures.",
    days: [
      "Emotional adjustment — Scaling Desires over Failures",
      "First emotional step — The Belief in Oneself",
      "Second emotional step — The Building of Endurance",
      "Third emotional step — The Embracement of Discipline",
      "Fourth emotional step — The Building of Humility",
      "Fifth emotional step — The Absence of Fear",
      "Sixth emotional step — The Knowledge of Growth",
    ],
  },
  {
    icon: Dumbbell,
    label: "Physical",
    range: "Days 15 — 21",
    intro: "The remembrance of the action.",
    days: [
      "Muscle memory — The Remembrance of the Action",
      "First physical step — The Application of the Goal",
      "Second physical step — The Manifestation",
      "Third physical step — The Tangible Experience",
      "Fourth physical step — The Realization of Effort",
      "Fifth physical step — The Understanding of Process",
      "Sixth physical step — The Beauty of Achievements",
    ],
  },
  {
    icon: Sparkles,
    label: "Spiritual",
    range: "Days 22 — 28",
    intro: "Retaining the fire within.",
    days: [
      "The Inner Spark — Retaining The Fire",
      "First spiritual step — The Power from Within",
      "Second spiritual step — The Spirit of Love",
      "Third spiritual step — The Spirit to Respond with Ability",
      "Fourth spiritual step — The Spirit of Letting Go",
      "Fifth spiritual step — The Spirit of Regret",
      "Sixth spiritual step — The Spirit of Personal Development",
    ],
  },
];

const closing = [
  {
    icon: Eye,
    label: "Day 29",
    title: "Looking Through a New Lens",
    body: "Seeing yourself, your circle and your work with fresh perception after 28 days of structured growth.",
  },
  {
    icon: GalleryVerticalEnd,
    label: "Day 30",
    title: "The Mirror of Life",
    body: "The final reflection. Who looks back at you now — and what they refuse to go back to.",
  },
];

export default function Journey() {
  return (
    <section
      id="journey"
      data-testid="journey-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37]/80">
              The 30-day arc
            </p>
            <h2 className="font-serif-display mt-4 text-4xl sm:text-5xl text-white leading-[1.05] tracking-tight">
              Four pillars. Thirty chapters.{" "}
              <em className="gold-text">One transformation.</em>
            </h2>
          </div>
          <p className="text-sm text-neutral-400 max-w-sm">
            Seven days of Mental. Seven Emotional. Seven Physical. Seven
            Spiritual. Two closing reflections. The arc is sequenced — not
            optional.
          </p>
        </div>

        {/* 4 Pillars */}
        <div className="grid md:grid-cols-2 gap-5">
          {pillars.map(({ icon: Icon, label, range, intro, days }, i) => (
            <div
              key={i}
              data-testid={`pillar-card-${i}`}
              className="group relative p-8 rounded-2xl bg-gradient-to-br from-[#141414] to-[#0e0e0e] border border-white/5 hover:border-[#d4af37]/25 transition-all duration-500"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/20 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#d4af37]" />
                  </div>
                  <div>
                    <span className="text-[11px] uppercase tracking-[0.25em] text-[#d4af37]/80 block">
                      {range}
                    </span>
                    <h3 className="font-serif-display text-2xl text-white leading-tight">
                      {label} <span className="text-neutral-500 text-base font-sans">days</span>
                    </h3>
                  </div>
                </div>
                <span className="font-serif-display text-4xl gold-text">
                  0{i + 1}
                </span>
              </div>

              <p className="mt-5 text-sm text-neutral-400 italic font-serif-display text-base">
                &ldquo;{intro}&rdquo;
              </p>

              <ol className="mt-6 space-y-2.5">
                {days.map((d, j) => {
                  const [head, tail] = d.split(" — ");
                  return (
                    <li
                      key={j}
                      className="flex gap-3 text-sm text-neutral-300 leading-snug"
                    >
                      <span className="shrink-0 w-6 text-right font-serif-display text-[#d4af37]/70">
                        {String(j + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1">
                        <span className="text-neutral-200">{head}</span>
                        {tail && (
                          <span className="text-neutral-500"> — {tail}</span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </div>
          ))}
        </div>

        {/* Closing chapters */}
        <div className="mt-5 grid md:grid-cols-2 gap-5">
          {closing.map(({ icon: Icon, label, title, body }, i) => (
            <div
              key={i}
              data-testid={`closing-card-${i}`}
              className="relative p-8 rounded-2xl border border-[#d4af37]/20 bg-[#0f0d07] hover:border-[#d4af37]/40 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#d4af37]/15 border border-[#d4af37]/30 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#e5c158]" />
                </div>
                <span className="text-[11px] uppercase tracking-[0.3em] text-[#e5c158]">
                  {label} · Closing chapter
                </span>
              </div>
              <h3 className="font-serif-display mt-5 text-3xl text-white">
                {title}
              </h3>
              <p className="mt-3 text-sm text-neutral-400 leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
